import React from 'react';
import { X } from 'lucide-react';

/**
 * StandardPremiumModal - Consistent premium modal with fixed dimensions
 * 
 * Features:
 * - Fixed size: 900px × 720px (desktop)
 * - Sticky header, tabs, and footer
 * - Scroll only in content area
 * - Premium visual design
 * - Responsive (full screen on mobile)
 */
const StandardPremiumModal = ({ 
  isOpen, 
  onClose, 
  title, 
  children,
  footer,
  tabs,
  activeTab,
  onTabChange,
  size = 'standard' // standard (900px), large (1040px), xlarge (1200px)
}) => {
  if (!isOpen) return null;

  const modalWidth = size === 'xlarge' ? 'max-w-[1200px]' : size === 'large' ? 'max-w-[960px]' : 'max-w-[920px]';

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 sm:p-6 bg-black/50 backdrop-blur-sm animate-backdropFadeIn">
      <div 
        className={`bg-white rounded-2xl shadow-2xl w-full ${modalWidth} flex flex-col overflow-hidden animate-modalSlideIn`}
        style={{
          height: '80vh',
          maxHeight: 'calc(100vh - 64px)',
          boxShadow: '0 20px 40px -5px rgba(0, 0, 0, 0.15), 0 10px 15px -5px rgba(0, 0, 0, 0.08)'
        }}
      >
        {/* Header - Sticky */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 bg-white flex-shrink-0">
          <h2 className="text-lg font-semibold text-gray-900">{title}</h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors p-1 rounded-lg hover:bg-gray-100"
            aria-label="Close modal"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Tabs - Sticky (if provided) */}
        {tabs && tabs.length > 0 && (
          <div className="flex border-b border-gray-200 bg-white px-6 flex-shrink-0 overflow-x-auto">
            {tabs.map(tab => (
              <button
                key={tab.id}
                onClick={() => onTabChange(tab.id)}
                className={`px-4 py-3 text-[13px] font-medium transition-colors relative whitespace-nowrap ${
                  activeTab === tab.id
                    ? 'text-teal-600 border-b-2 border-teal-600'
                    : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                {tab.label}
              </button>
            ))}
          </div>
        )}

        {/* Content - Scrollable */}
        <div className="flex-1 overflow-y-auto px-6 py-6">
          {children}
        </div>

        {/* Footer - Sticky */}
        {footer && (
          <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-gray-200 bg-white flex-shrink-0">
            {footer}
          </div>
        )}
      </div>
    </div>
  );
};

export default StandardPremiumModal;
